import ky from "ky";
import { env } from "./env";
import {
  Authorization,
  Config,
  Profile,
  RouteCard,
  RouteInstrumentRequest,
  Order,
  CreateOrderRequest,
  RouteQuote,
  RouteQuoteRequest,
  Preorder,
  CoinbaseOnrampOrderRequest,
  CoinbaseOnrampOrder,
  CoinbaseOnrampLimits,
  CoinbaseOnrampLimitUpgradeRequest,
} from "./types";
import { getAuth, removeAuth } from "./local";
import z from "zod";
import { signatureManager } from "./signature";

function base64UrlEncode(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

const RESPONSE_SCHEMA = z.union([
  z.object({
    type: z.literal("error").default("error"),
    error: z.object({
      code: z.number(),
      description: z.string(),
      extra: z.unknown().optional(),
    }),
  }),
  z.object({
    type: z.literal("data").default("data"),
    data: z.unknown().nullable(),
  }),
]);

export class ApiError extends Error {
  constructor(
    code: number,
    description?: string,
    extra?: unknown,
    request?: Request,
  ) {
    super(`code: ${code}, description: ${description}, request: ${request}`);
    this.name = "ApiError";
    this.code = code;
    this.description = description;
    this.extra = extra;
  }
  readonly code: number;
  readonly description?: string;
  readonly extra?: unknown;
}

const client = ky.create({
  prefixUrl: env.VITE_ROUTE_SERVER + "/",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  hooks: {
    beforeRequest: [
      (req) => {
        const token = getAuth()?.token;
        if (token) {
          req.headers.set("Authorization", `Bearer ${token}`);
        }
      },
      async (request) => {
        const url = new URL(request.url);
        if (!url.pathname.startsWith("/ramp/") && url.pathname !== "/ramp") {
          return;
        }

        try {
          if (import.meta.env.DEV && env.VITE_DEV_USER_ID) {
            request.headers.set(
              "MR-ACCESS-TIMESTAMP",
              String(Math.floor(Date.now() / 1000)),
            );
            request.headers.set(
              "MR-ACCESS-SIGN",
              base64UrlEncode(env.VITE_DEV_USER_ID),
            );
            return;
          }

          const method = request.method;
          const body =
            request.method !== "GET" && request.method !== "HEAD"
              ? await request.clone().text()
              : "";

          console.log("Getting signature for request", {
            method,
            url: request.url,
            body,
          });

          const response = await signatureManager.getSignature({
            method,
            url: request.url,
            body,
          });

          console.log("Received signature for request", response);

          request.headers.set("MR-ACCESS-TIMESTAMP", response.timestamp);
          request.headers.set("MR-ACCESS-SIGN", response.signature);
        } catch (error) {
          console.error("Failed to get signature:", error);
          throw new Error("Failed to get signature for request");
        }
      },
    ],
    afterResponse: [
      async (req, _, res) => {
        const json = await res.json();
        const rsp = RESPONSE_SCHEMA.safeParse(json);
        if (!rsp.success)
          throw new ApiError(500, "Invalid response", rsp.error);
        if (rsp.data.type === "error") {
          const error = rsp.data.error;
          if (error.code === 401) {
            removeAuth();
            if (window.location.pathname.startsWith("/auth")) return;
            window.location.replace("/auth");
          }
          throw new ApiError(error.code, error.description, error.extra, req);
        }

        return new Response(JSON.stringify(rsp.data.data), {
          headers: {
            "Content-Type": "application/json",
          },
        });
      },
    ],
  },
});

export const auth = (code: string) =>
  client.post("auth", { json: { code } }).json<Authorization>();

export const createAuthEmailSession = (email: string) =>
  client
    .post("auth/email", { json: { email } })
    .json<{ verification_id: string }>();

export const authByEmail = (json: {
  verification_id: string;
  email: string;
  code: string;
}) => client.post("verifications", { json }).json<Authorization>();

export const getProfile = () =>
  client.get("profile?version=1.6.0").json<Profile>();
export const getConfig = () => client.get("config").json<Config>();
export const getQuote = (req: RouteQuoteRequest) =>
  client.post("quote", { json: req }).json<RouteQuote>();
export const getInstruments = () =>
  client.get("checkout/instruments").json<RouteCard[]>();
export const removeInstrument = (id: string) =>
  client.delete(`checkout/instruments/${id}`).json();
export const addInstrument = (req: RouteInstrumentRequest) =>
  client.post("checkout/instruments", { json: req }).json<RouteCard>();
export const addOrder = (req: CreateOrderRequest) =>
  client.post(`orders`, { json: req }).json<Order>();
export const getOrder = (id: string) =>
  client.get(`orders/${id}`).json<Order>();
export const pay = (orderId: string, deviceSessionId: string) =>
  client
    .post(`orders/${orderId}/payment`, {
      json: {
        device_session_id: deviceSessionId,
      },
    })
    .json<Order>();
export const updateOrderPrice = (orderId: string, assetAmount: string) =>
  client
    .post(`orders/${orderId}/price`, {
      json: { asset_amount: assetAmount },
    })
    .json<Order>();

export const getOrders = (limit: number, offset: string) =>
  client.get("orders", { searchParams: { limit, offset } }).json<Order[]>();

export const sumsubToken = () =>
  client.get("kyc/token").json<{
    token: string;
    state: "initial" | "pending" | "success" | "retry" | "blocked" | "ignore";
  }>();

export const getKycLink = () =>
  client.get("kyc/link").json<{ url: string }>();

export const preorder = (preorderReq: Preorder) =>
  client
    .post("preorder", {
      json: {
        ...preorderReq,
        amount: preorderReq.amount ? preorderReq.amount + "" : undefined,
      },
    })
    .json<{ authorization_token: string }>();

export const checkAddress = (json: { asset_id: string; destination: string }) =>
  client.post("addresses/check", { json }).json<{ valid: boolean }>();

// Mixin Network API
import { NetworkAsset, NetworkChain } from "./types";

export const getNetworkAsset = (assetId: string) =>
  ky.get(`https://api.mixin.one/network/assets/${assetId}`).json<{
    data: NetworkAsset;
  }>();

export const getNetworkChains = () =>
  ky.get("https://api.mixin.one/network/chains").json<{
    data: NetworkChain[];
  }>();

export const createCoinbaseOnrampOrder = (req: CoinbaseOnrampOrderRequest) => {
  console.log("createCoinbaseOnrampOrder request", req);
  return client
    .post("ramp/orders", {
      json: req,
      prefixUrl: env.VITE_ROUTE_SERVER + "/",
    })
    .json<CoinbaseOnrampOrder>()
    .then((r) => {
      console.log("createCoinbaseOnrampOrder response", r);
      return r;
    });
};

export const getCoinbaseOnrampOrder = (orderId: string) =>
  client
    .get(`ramp/orders/${orderId}`, {
      prefixUrl: env.VITE_ROUTE_SERVER + "/",
    })
    .json<CoinbaseOnrampOrder>();

export const getCoinbaseOnrampLimits = (phone: string) =>
  client
    .post("ramp/coinbase/limits", {
      json: { phone },
      prefixUrl: env.VITE_ROUTE_SERVER + "/",
    })
    .json<CoinbaseOnrampLimits>();

export const submitCoinbaseOnrampLimitsUpgrade = (
  req: CoinbaseOnrampLimitUpgradeRequest,
) =>
  client
    .post("ramp/coinbase/limits/upgrade", {
      json: req,
      prefixUrl: env.VITE_ROUTE_SERVER + "/",
    })
    .json<void>();
